import { ReactNode } from 'react';

interface SubtitleMetadata {
  entityType: string;
  entityName: string;
  mainTimePeriodName: string;
  comparisonTimePeriodName: string;
  categoryCount: number;
}

type SubtitlePart = string | ReactNode | (string | ReactNode)[];
type SubtitleTextInput = SubtitlePart | ((params: SubtitleMetadata) => SubtitlePart);

/**
 * Class to build a dynamic subtitle for a visualization
 */
export class DynamicSubtitle {
  private parts: SubtitlePart[];
  private metadata: SubtitleMetadata;

  constructor(metadata: SubtitleMetadata) {
    this.parts = [];
    this.metadata = metadata;
  }

  public setMainTimePeriodName(name: string): DynamicSubtitle {
    this.metadata.mainTimePeriodName = name;
    return this;
  }

  public setComparisonTimePeriodName(name: string): DynamicSubtitle {
    this.metadata.comparisonTimePeriodName = name;
    return this;
  }

  public updateMetadata(metadata: Partial<SubtitleMetadata>): DynamicSubtitle {
    this.metadata = {
      ...this.metadata,
      ...metadata
    };
    return this;
  }

  public addText(args: SubtitleTextInput): DynamicSubtitle {
    if (!args) {
      return this;
    }

    let processedText: SubtitlePart;
    if (typeof args === 'function') {
      processedText = args(this.metadata);
    } else {
      processedText = args;
    }

    this.parts.push(processedText);
    this.addSpace();
    return this;
  }

  public addSpace(): DynamicSubtitle {
    this.parts.push(' ');
    return this;
  }

  public addPunctuation(mark: '.' | ',' | '!' | '?' | ';'): DynamicSubtitle {
    // Remove trailing space if it exists
    if (this.parts[this.parts.length - 1] === ' ') {
      this.parts.pop();
    }
    this.parts.push(mark);

    // Add space after comma and semicolon
    if (mark === ',' || mark === ';') {
      this.addSpace();
    }

    return this;
  }

  public build(): ReactNode[] {
    return this.parts.filter((part) => part !== undefined && part !== '');
  }
}
